/**
 * Base service utilities
 * Shared functions for all API services
 */

export const BASE_URL = import.meta.env['VITE_API_BASE_URL'];

export class ApiError extends Error {
  public status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Generic authenticated request helper
 * Used by all service modules
 */
export async function authenticatedRequest<T = unknown>(
  url: string,
  options: RequestInit = {},
  token: string
): Promise<T> {
  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Token expired or invalid - clear storage and throw error
    localStorage.removeItem('finara_token');
    localStorage.removeItem('finara_token_expires');
    localStorage.removeItem('finara_user_id');
    throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
  }

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(errorData.message ?? 'Error en la solicitud', response.status);
  }

  // Handle responses with no content (204, etc.)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  // Check if response has JSON content
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  // If no JSON content, return undefined
  return undefined as T;
}
